// Story E0.1.3 (FR-E0-5): Badge de mensagem não-lida no menu lateral do chat.
//
// Convenções visuais (alinhadas ao spec):
//   - count > 0 e sem mention   → fundo azul (variant default), texto branco.
//   - has_unread_mentions=true  → fundo vermelho (variant destructive).
//                                  Sobrepõe o azul mesmo com count baixo —
//                                  mention é prioridade visual.
//   - silenciada (D2, passo 14)  → fundo cinza (variant secondary): "conta, mas não
//                                  grita". A menção VENCE o cinza (continua vermelha),
//                                  porque é ela que avisa mesmo numa conversa silenciada.
//   - count === 0                → não renderiza nada (componente retorna null).
//   - count > 99                 → mostra "99+" (cap visual; preserva sense
//                                  of magnitude sem alargar o badge).
//   - count === 1 vs > 1         → sempre mostra o número (o spec original
//                                  diz "número se >1", mas mostrar "1" é
//                                  consistente com Slack/Discord/Telegram e
//                                  evita a ambiguidade "bolinha sem número
//                                  vs com número").
//
// A11y:
//   - ``aria-label`` localizado em pt-BR descrevendo contagem + se há mention + se a
//     conversa está silenciada (a cor cinza sozinha não diz nada a quem não a vê).
//   - Renderiza como ``<span role="status">`` pra screen readers tratarem como
//     live region (mudança de count anuncia sem ser intrusivo).
//
// Reuso: usa ``<Badge>`` do shadcn/ui (já presente no projeto via
// ``@/components/ui/badge``), só trocando ``variant`` por estado.

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const MAX_DISPLAY = 99;

export interface UnreadBadgeProps {
  count: number;
  hasMentions?: boolean;
  /** Conversa silenciada: o selo fica cinza e o `aria-label` diz o estado (a menção vence o cinza). */
  silenciada?: boolean;
  /**
   * O `aria-label` inteiro, quando o número não é de mensagens (ex.: "3 posts novos" no item do Feed, ou a
   * frase somada do botão "Menu" do celular). Sem ele vale o texto de sempre, "N mensagens não lidas", com
   * os avisos de menção e de silenciada. Com ele, quem chama diz tudo: nada é acrescentado.
   */
  rotulo?: string;
  className?: string;
}

/**
 * Render condicional do badge de não-lidas.
 *
 * Retorna ``null`` quando count <= 0 — caller não precisa de guard externo.
 * É o selo vivo da lista de conversas (``RoomRow``, em ``ChatRoomList``).
 */
export function UnreadBadge({
  count,
  hasMentions = false,
  silenciada = false,
  rotulo,
  className,
}: UnreadBadgeProps) {
  if (!count || count <= 0) return null;

  const display = count > MAX_DISPLAY ? `${MAX_DISPLAY}+` : String(count);
  const ariaLabel =
    rotulo ||
    `${count} ${count === 1 ? "mensagem não lida" : "mensagens não lidas"}` +
      (hasMentions ? " (com menção)" : "") +
      (silenciada ? " (conversa silenciada)" : "");

  return (
    <Badge
      variant={hasMentions ? "destructive" : silenciada ? "secondary" : "default"}
      role="status"
      aria-label={ariaLabel}
      className={cn("h-5 min-w-5 px-1.5 text-xs tabular-nums", className)}
    >
      {display}
    </Badge>
  );
}
